import React from 'react';
import { T, PART_META } from './_theme';
import { ReadingFrame, PrimaryBtn, GhostBtn, readingHomeHref } from './_primitives';

// Official-looking post-exam result, modelled after a real FET reading exam
// score sheet. Deliberately cold and numeric — no per-question feedback, no
// translations, no drill suggestions, no vocabulary. The student gets the
// same view they'd see on the day.
//
// If they want to study (mistakes / vocabulary / training drills) they can
// open those tabs from Reading home — exam mode also writes per-part progress
// to localStorage so the practice extras pick up exam attempts automatically.

// FET reading band cut-offs (CEFR-style). 16-17/30 ≈ pass on a typical paper.
const BANDS = [
  { min: 85, label: 'Advanced',         cefr: 'C1', tone: '#16a34a' },
  { min: 70, label: 'Intermediate',     cefr: 'B2', tone: '#22c55e' },
  { min: 55, label: 'Pre-Intermediate', cefr: 'B1', tone: '#3b82f6' },
  { min: 40, label: 'Elementary',       cefr: 'A2', tone: '#f59e0b' },
  { min:  0, label: 'Beginner',         cefr: 'A1', tone: '#ef4444' },
];

// Authoritative gallery titles for the 5-part FET reading layout.
const PART_NAMES = {
  1: 'Signs & Notices',
  2: 'Gap Fill',
  3: 'Text Matching',
  4: 'People ↔ Place',
  5: 'Long Reading',
};

const fmtDuration = (s) => {
  const m = Math.floor(s / 60);
  const ss = s % 60;
  if (m === 0) return `${ss}s`;
  if (ss === 0) return `${m} min`;
  return `${m} min ${ss}s`;
};

export default function ExamReport({ result, parts, isMobile, navigate }) {
  const { total, max, percentage, part_scores: partScores = [], durationSeconds = 0 } = result || {};
  const band = BANDS.find((b) => percentage >= b.min) || BANDS[BANDS.length - 1];
  const passed = percentage >= 55;
  const dateStr = new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

  // Match each scored part to its content metadata (for the part name).
  // Defensive: drop legacy Part 6 rows if they sneak through.
  const rows = (partScores.length ? partScores : (parts || []).map((p) => ({ part_number: p.part_number, score: 0, max: 0 })))
    .slice()
    .filter((r) => Number(r.part_number) >= 1 && Number(r.part_number) <= 5)
    .sort((a, b) => a.part_number - b.part_number)
    .map((ps) => ({ ...ps, name: PART_NAMES[ps.part_number] || `Part ${ps.part_number}` }));

  const partColor = (n) => (PART_META.find((m) => m.n === n) || {}).color || T.blue;

  return (
    <ReadingFrame isMobile={isMobile}>
      <div style={{ padding: 16 }}>
        {/* Result hero — exam paper style */}
        <div style={{
          background: T.surface,
          border: `1px solid ${T.hairline}`,
          borderRadius: 18,
          marginBottom: 14,
          overflow: 'hidden',
          fontFamily: T.sans,
        }}>
          <div style={{
            padding: '10px 18px',
            background: passed ? T.ok : T.miss,
            color: '#fff',
            fontSize: 11, fontWeight: 800, letterSpacing: '.18em',
            textTransform: 'uppercase', textAlign: 'center',
          }}>
            {passed ? 'Result · PASS' : 'Result · NOT YET PASSED'}
          </div>

          <div style={{ padding: isMobile ? '22px 18px' : '32px 28px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.16em', textTransform: 'uppercase', color: T.ink3 }}>
                  FET Reading Exam
                </div>
                <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 800, color: T.ink, marginTop: 4, letterSpacing: '-.3px' }}>
                  Score Report
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: T.ink3, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase' }}>Date</div>
                <div style={{ fontSize: 13, color: T.ink, fontWeight: 700 }}>{dateStr}</div>
              </div>
            </div>

            {/* Total score */}
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 6, marginBottom: 8 }}>
              <span style={{ fontSize: isMobile ? 56 : 72, fontWeight: 900, color: band.tone, lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: '-2px' }}>{total}</span>
              <span style={{ fontSize: isMobile ? 22 : 28, color: T.ink3, fontWeight: 700 }}>/ {max}</span>
            </div>
            <div style={{ textAlign: 'center', fontSize: 14, color: T.ink2, marginBottom: 16 }}>
              <strong style={{ color: T.ink }}>{percentage}%</strong> overall
            </div>

            {/* Stats row */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              gap: 1,
              background: T.hairline,
              border: `1px solid ${T.hairline}`,
              borderRadius: 10,
              overflow: 'hidden',
              marginBottom: 4,
            }}>
              <Stat label="Band" value={band.cefr} hint={band.label} />
              <Stat label="Time taken" value={fmtDuration(durationSeconds)} hint={`of 45 min allowed`} />
              <Stat label="Correct" value={`${total} / ${max}`} hint={null} />
              <Stat label="Wrong" value={`${Math.max(0, max - total)}`} hint={null} />
            </div>
          </div>
        </div>

        {/* Per-part scoring table */}
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.16em', textTransform: 'uppercase', color: T.ink3, marginBottom: 8, fontFamily: T.sans }}>
          Score by part
        </div>
        <div style={{
          background: T.surface,
          border: `1px solid ${T.hairline}`,
          borderRadius: 14,
          overflow: 'hidden',
          marginBottom: 18,
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '60px 1fr 90px 110px',
            background: T.bgSunk,
            padding: '10px 16px',
            fontSize: 10, fontWeight: 800, letterSpacing: '.1em',
            textTransform: 'uppercase', color: T.ink3,
            borderBottom: `1px solid ${T.hairline}`,
            fontFamily: T.sans,
          }}>
            <div>Part</div>
            <div>Section</div>
            <div style={{ textAlign: 'right' }}>Score</div>
            <div style={{ textAlign: 'right' }}>%</div>
          </div>
          {rows.map((r, i) => {
            const accent = partColor(r.part_number);
            const pct = r.max ? Math.round((r.score / r.max) * 100) : 0;
            return (
              <div key={r.part_number} style={{
                display: 'grid',
                gridTemplateColumns: '60px 1fr 90px 110px',
                alignItems: 'center',
                padding: '12px 16px',
                borderBottom: i === rows.length - 1 ? 'none' : `1px solid ${T.hairline}`,
                fontFamily: T.sans,
              }}>
                <div>
                  <span style={{
                    width: 30, height: 30, borderRadius: 8,
                    background: `${accent}1a`, color: accent,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 900, fontSize: 13,
                  }}>{r.part_number}</span>
                </div>
                <div style={{ fontSize: 13, color: T.ink, fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.name}
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color: T.ink, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {r.score}/{r.max}
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color: pct >= 60 ? T.ok : pct >= 40 ? T.hint : T.miss, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {pct}%
                </div>
              </div>
            );
          })}
          {/* Totals row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '60px 1fr 90px 110px',
            alignItems: 'center',
            padding: '12px 16px',
            background: T.bgSunk,
            borderTop: `1px solid ${T.hairline}`,
            fontFamily: T.sans,
          }}>
            <div />
            <div style={{ fontSize: 12, fontWeight: 800, color: T.ink3, letterSpacing: '.06em', textTransform: 'uppercase' }}>Total</div>
            <div style={{ fontSize: 14, fontWeight: 900, color: T.ink, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
              {total}/{max}
            </div>
            <div style={{ fontSize: 14, fontWeight: 900, color: band.tone, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
              {percentage}%
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <GhostBtn onClick={() => window.location.reload()}>Retake exam</GhostBtn>
          <PrimaryBtn full={false} onClick={() => navigate(readingHomeHref())}>
            Back to reading home
          </PrimaryBtn>
        </div>

        <div style={{ fontSize: 11, color: T.ink3, textAlign: 'center', marginTop: 18, lineHeight: 1.6, fontFamily: T.sans }}>
          To review your wrong answers, vocabulary or training drills, open the relevant tile on the Reading home page.
        </div>
      </div>
    </ReadingFrame>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div style={{
      background: T.surface,
      padding: '12px 14px',
      textAlign: 'center',
      fontFamily: T.sans,
    }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: T.ink3, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 900, color: T.ink, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', letterSpacing: '-.3px' }}>
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 10, color: T.ink3, marginTop: 3, fontWeight: 600 }}>
          {hint}
        </div>
      )}
    </div>
  );
}
